import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { useFeatureAccess } from './useQuota';
import { incrementUsage } from '../utils/firestoreApi';
import { QuotaErrorHandler, QuotaError } from '../utils/quotaErrorHandler';
import { UsageType } from '../utils/quotaConfig';

interface QuotaEnforcementResult {
  success: boolean;
  error?: QuotaError;
}

interface UseQuotaEnforcementReturn {
  /**
   * Check if user can perform an action that requires quota
   */
  canPerformAction: (usageType: UsageType) => Promise<boolean>;
  
  /**
   * Perform an action with quota enforcement
   * Returns success status and any errors
   */
  performWithQuotaCheck: (
    usageType: UsageType, 
    action: () => Promise<void> | void
  ) => Promise<QuotaEnforcementResult>;
  
  /**
   * Increment quota usage after successful action
   */
  incrementQuotaUsage: (usageType: UsageType) => Promise<QuotaEnforcementResult>;
}

export const useQuotaEnforcement = (): UseQuotaEnforcementReturn => {
  const { user } = useAuth();
  const { canSendChatMessage, canCreateThreadStarter, canCreateThreadIdea, canUploadFile, canPerformWebSearch, canCreateCanvas } = useFeatureAccess();

  const getQuotaCheckFunction = useCallback((usageType: UsageType) => {
    switch (usageType) {
      case 'monthlyChatMessages':
        return canSendChatMessage;
      case 'threadStarters':
        return canCreateThreadStarter;
      case 'totalThreads':
        return canCreateThreadIdea;
      case 'fileAttachments':
        return canUploadFile;
      case 'webSearches':
        return canPerformWebSearch;
      case 'totalCanvases':
        return canCreateCanvas;
      default:
        return async () => true;
    }
  }, [canSendChatMessage, canCreateThreadStarter, canCreateThreadIdea, canUploadFile, canPerformWebSearch, canCreateCanvas]);

  const canPerformAction = useCallback(async (usageType: UsageType): Promise<boolean> => {
    if (!user?.uid) {
      return false;
    }

    try {
      const checkFunction = getQuotaCheckFunction(usageType);
      return await checkFunction();
    } catch (error) {
      const quotaError = QuotaErrorHandler.handleQuotaCheckError(error, usageType);
      console.error('Quota check failed:', quotaError);
      // Allow action on error to prevent blocking users
      return true;
    }
  }, [user?.uid, getQuotaCheckFunction]);

  const performWithQuotaCheck = useCallback(async (
    usageType: UsageType,
    action: () => Promise<void> | void
  ): Promise<QuotaEnforcementResult> => {
    if (!user?.uid) {
      return {
        success: false,
        error: QuotaErrorHandler.createQuotaExceededError(usageType, false)
      };
    }

    try {
      // Check quota before performing action
      const canPerform = await canPerformAction(usageType);
      if (!canPerform) {
        return {
          success: false,
          error: QuotaErrorHandler.createQuotaExceededError(usageType, true)
        };
      }

      // Perform the action
      await action();

      // Increment quota after successful action
      const incrementResult = await incrementQuotaUsage(usageType);
      if (!incrementResult.success) {
        // Action succeeded but quota increment failed - log but don't block
        console.warn('Action completed but quota increment failed:', incrementResult.error);
      }

      return { success: true };
    } catch (error) {
      const quotaError = QuotaErrorHandler.handleQuotaCheckError(error, usageType);
      return {
        success: false,
        error: quotaError
      };
    }
  }, [user?.uid, canPerformAction]);

  const incrementQuotaUsage = useCallback(async (usageType: UsageType): Promise<QuotaEnforcementResult> => {
    if (!user?.uid) {
      return {
        success: false,
        error: QuotaErrorHandler.createQuotaExceededError(usageType, false)
      };
    }

    try {
      await incrementUsage(user.uid, usageType);
      return { success: true };
    } catch (error) {
      const quotaError = QuotaErrorHandler.handleQuotaIncrementError(error, usageType);
      return {
        success: false,
        error: quotaError
      };
    }
  }, [user?.uid]);

  return {
    canPerformAction,
    performWithQuotaCheck,
    incrementQuotaUsage
  };
};
